import React from "react";
import { Image } from "./components/atoms/Image";
import { Box, Typography, Button } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./theme/muiTheme";

const DebugImageTest: React.FC = () => {
  const [testUrl, setTestUrl] = React.useState<string>("");
  const [testKey, setTestKey] = React.useState<number>(0);

  // Sample image URLs for testing
  const sampleUrls = [
    "https://picsum.photos/300/300?random=1",
    "https://picsum.photos/300/300?random=2",
    "https://via.placeholder.com/300x300/FF0000/FFFFFF?text=Test+Image+1",
    "https://via.placeholder.com/300x300/00FF00/FFFFFF?text=Test+Image+2",
    "https://example.com/test-no-flicker/sample-image.jpg", // This will trigger test mode
    "https://picsum.photos/300/300?random=3",
  ];

  const handleTestImage = (url: string) => {
    setTestUrl(url);
    setTestKey((prev) => prev + 1); // Force re-render
  };

  const handleRefresh = () => {
    setTestKey((prev) => prev + 1);
  };

  return (
    <ThemeProvider theme={createAppTheme("light")}>
      <Box sx={{ padding: 4 }}>
        <Typography variant="h4" gutterBottom>
          🐛 Image Flickering Debug Test
        </Typography>

        <Typography variant="body1" gutterBottom>
          This page helps debug image flickering issues. Watch the console for
          debug logs.
        </Typography>

        <Box sx={{ marginBottom: 4 }}>
          <Typography variant="h6" gutterBottom>
            Test URLs:
          </Typography>
          {sampleUrls.map((url, index) => (
            <Button
              key={index}
              variant="outlined"
              onClick={() => handleTestImage(url)}
              sx={{ marginRight: 1, marginBottom: 1 }}
              size="small"
            >
              {url.includes("test-no-flicker")
                ? "Test Mode"
                : `Test ${index + 1}`}
            </Button>
          ))}

          <Button
            variant="contained"
            onClick={handleRefresh}
            sx={{ marginLeft: 2 }}
            size="small"
          >
            Refresh Current
          </Button>
        </Box>

        {testUrl && (
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h6" gutterBottom>
              Current Test URL: {testUrl}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: 2,
                marginTop: 2,
              }}
            >
              {/* Test different configurations */}
              <Box
                sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Default Settings
                </Typography>
                <Image
                  key={`default-${testKey}`}
                  src={testUrl}
                  alt="Test Image - Default"
                  width={300}
                  height={200}
                  responsive={false}
                />
              </Box>

              <Box
                sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  No Cloudinary Optimization
                </Typography>
                <Image
                  key={`no-cloudinary-${testKey}`}
                  src={testUrl}
                  alt="Test Image - No Cloudinary"
                  width={300}
                  height={200}
                  responsive={false}
                  optimizeWithCloudinary={false}
                />
              </Box>

              <Box
                sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  No Loading State
                </Typography>
                <Image
                  key={`no-loading-${testKey}`}
                  src={testUrl}
                  alt="Test Image - No Loading"
                  width={300}
                  height={200}
                  responsive={false}
                  showLoadingState={false}
                />
              </Box>

              <Box
                sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Responsive Mode
                </Typography>
                <Image
                  key={`responsive-${testKey}`}
                  src={testUrl}
                  alt="Test Image - Responsive"
                  width={200}
                  height={200}
                  responsive={true}
                />
              </Box>

              <Box
                sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  With Shimmer Effect
                </Typography>
                <Image
                  key={`shimmer-${testKey}`}
                  src={testUrl}
                  alt="Test Image - Shimmer"
                  width={300}
                  height={200}
                  responsive={false}
                />
              </Box>

              <Box
                sx={{ border: "1px solid #ccc", padding: 2, borderRadius: 1 }}
              >
                <Typography variant="subtitle1" gutterBottom>
                  Minimal Configuration
                </Typography>
                <Image
                  key={`minimal-${testKey}`}
                  src={testUrl}
                  alt="Test Image - Minimal"
                  width={300}
                  height={200}
                  responsive={false}
                  optimizeWithCloudinary={false}
                  showLoadingState={false}
                />
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ marginTop: 4 }}>
          <Typography variant="h6" gutterBottom>
            Debug Information:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Open browser console to see debug logs • Look for "🖼️ Image
            Debug:" messages • Watch for state changes and URL switches • Test
            images with "test-no-flicker" in URL will use test mode
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DebugImageTest;
